import {
  forwardRef,
  memo,
  useCallback,
  useMemo,
  type ReactNode,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type DragEvent,
  JSX,
} from "react";
import DashboardWidgetContent from "src/components/builder/DashboardWidgetContent";
import WidgetConfigMenu from "src/components/builder/WidgetConfigMenu";
import {
  hasWidgetConfigFields,
  toConfigOverride,
  type WidgetInstanceConfig,
} from "src/charts/widgetConfig";
import useBuilderStore, {
  type BuilderCanvasItem,
} from "src/store/builderStore";
import { BUILDER_WIDGET_DUMMY } from "src/config/dummy/widgets";

type BuilderWidgetDataEntry = {
  data?: unknown;
  [key: string]: unknown;
};

// One file per widget code under config/dummy/widgets — see that folder's
// index.ts. Previously a single 176k-line JSON, which was slow to open.
const BUILDER_WIDGET_DATA_BY_ID = BUILDER_WIDGET_DUMMY as Record<
  string,
  BuilderWidgetDataEntry
>;

// Exported so other builder surfaces (e.g. Sidebar's hover preview) can look
// up the same realistic dummy payload for a widget code without needing a
// live dashboard/backend — this is the exact same resolution the canvas
// preview below uses.
export const resolveBuilderWidgetData = (widgetTemplateId: string): unknown => {
  if (!widgetTemplateId) return null;

  const widgetPayload = BUILDER_WIDGET_DATA_BY_ID[widgetTemplateId];

  if (!widgetPayload || typeof widgetPayload !== "object") return null;

  if (!Object.prototype.hasOwnProperty.call(widgetPayload, "data")) {
    return widgetPayload;
  }

  const payload = widgetPayload as {
    data?: unknown;
    metadata?: Record<string, unknown>;
    chart_type?: string;
  };

  if (payload.chart_type === "tiered_bar") {
    return {
      records: Array.isArray(payload.data)
        ? payload.data
        : (payload.data as any)?.records ?? [],
      tier_rules: payload.metadata?.tier_rules ?? {},
    };
  }

  if (payload.chart_type === "special_quadrant_chart") {
    return {
      practices: Array.isArray(payload.data) ? payload.data : [],
      kpi: (payload as any).kpi ?? {},
      tier_rules: payload.metadata?.tier_rules ?? {},
    };
  }

  // These widgets read metadata (totals, limits) alongside the rows.
  if (
    payload.chart_type === "donut_pie" ||
    payload.chart_type === "sentiment_split" ||
    payload.chart_type === "custom_lines" ||
    payload.chart_type === "custom_table" ||
    payload.chart_type === "deviation_bar" ||
    payload.chart_type === "average_bar" ||
    payload.chart_type === "cards" ||
    payload.chart_type === "bar_drilldown" ||
    // sub_bar reads metadata.min_decided_for_rate to explain its suppressed
    // rates, and expects the { data, metadata } envelope rather than bare
    // rows — unwrapping to payload.data left the builder preview showing
    // "No data available" for a payload that was actually fine.
    payload.chart_type === "sub_bar" ||
    // sentiment_scatter / sentiment_histogram read the backend's axis labels,
    // call totals and the truncated/clamped flags out of metadata — unwrapping
    // to the bare rows would silently drop all of it.
    payload.chart_type === "sentiment_scatter" ||
    payload.chart_type === "sentiment_histogram" ||
    // bubble_quadrant_chart reads axis labels, quadrant tint ranges, the
    // segment legend, and its filter specs out of metadata — same reasoning.
    payload.chart_type === "bubble_quadrant_chart"
  ) {
    return { data: payload.data ?? [], metadata: payload.metadata ?? {} };
  }

  return payload.data ?? null;
};

type SlotProps = {
  item: BuilderCanvasItem;
  isInteractionActive?: boolean;
  isRecentlyAdded?: boolean;
  isDropTarget: boolean;
  onDragOver: (event: DragEvent<HTMLElement>, itemId: string) => void;
  onDrop: (event: DragEvent<HTMLElement>, itemId: string) => void;
  onDragLeave: (itemId: string) => void;
  onRemove?: (itemId: string) => void;
  isWidgetFormInitiallyOpen?: boolean;
  onWidgetFormSaved?: (itemId: string) => void;
  children?: ReactNode;
} & Omit<
  ComponentPropsWithoutRef<"div">,
  "onDragOver" | "onDrop" | "onDragLeave"
>;

const Slot = forwardRef<HTMLDivElement, SlotProps>(function Slot(
  {
    item,
    isInteractionActive = false,
    isRecentlyAdded = false,
    isDropTarget,
    isWidgetFormInitiallyOpen = false,
    onWidgetFormSaved,
    onDragOver,
    onDrop,
    onDragLeave,
    onRemove,
    children,
    className,
    style,
    onMouseDown,
    onMouseUp,
    onTouchEnd,
    ...rest
  },
  ref,
): JSX.Element {
  const storedItem = useBuilderStore(
    (state) =>
      state.canvasItems.find((canvasItem) => canvasItem.i === item.i) ?? item,
  );
  const assignedWidget = useBuilderStore(
    (state) =>
      state.dashboardTemplates.find(
        (widget) => widget.id === storedItem.assignedWidgetId,
      ) ?? null,
  );
  const selectedDashboardCode = useBuilderStore(
    (state) => state.selectedDashboardCode,
  );
  const queueWidgetDeletion = useBuilderStore(
    (state) => state.queueWidgetDeletion,
  );
  const updateCanvasItemWidgetConfig = useBuilderStore(
    (state) => state.updateCanvasItemWidgetConfig,
  );

  const assignedWidgetCode = assignedWidget
    ? String(assignedWidget.code ?? "")
    : "";

  const resolvedWidgetData = assignedWidget
    ? resolveBuilderWidgetData(assignedWidgetCode)
    : null;

  const hasAssignment = !!storedItem.assignedWidgetId;

  const handleConfigChange = useCallback(
    (next: WidgetInstanceConfig) => {
      updateCanvasItemWidgetConfig(storedItem.i, { config: next });
    },
    [updateCanvasItemWidgetConfig, storedItem.i],
  );

  // The same shape the backend merges into chart_spec, so the preview below
  // renders through the author's settings exactly as the live dashboard will.
  // Memoised because DashboardWidgetContent is memo() and a fresh object each
  // render would defeat it.
  const previewChartSpec = useMemo(
    () => toConfigOverride(storedItem.config),
    [storedItem.config],
  );

  // Widgets seed their tab / hidden-chart state with useState, which only
  // reads its initial value — so changing a setting on an already-mounted
  // preview would otherwise do nothing visible until a reload. Folding the
  // config into the key remounts the preview on change, which is what makes
  // the gear menu feel live. Key order is fixed by toConfigOverride, so the
  // serialisation is stable.
  const previewConfigKey = useMemo(
    () => JSON.stringify(previewChartSpec),
    [previewChartSpec],
  );

  const handleRemove = () => {
    if (
      selectedDashboardCode &&
      storedItem.isServerWidgetAssignment &&
      storedItem.persistedWidgetId
    ) {
      queueWidgetDeletion(storedItem.persistedWidgetId);
    }

    onRemove?.(storedItem.i);
  };

  return (
    <div
      {...rest}
      ref={ref}
      className={`builder-live-grid__item${className ? ` ${className}` : ""}`}
      style={{ ...style } as CSSProperties}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onTouchEnd={onTouchEnd}
    >
      <article
        className={[
          "builder-canvas-card card",
          isDropTarget && "is-drop-target",
          (assignedWidget || hasAssignment) && "has-widget",
          isRecentlyAdded && "is-recently-added",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label={`${storedItem.label} slot`}
        onDragOver={(event) => onDragOver(event, storedItem.i)}
        onDrop={(event) => onDrop(event, storedItem.i)}
        onDragLeave={() => onDragLeave(storedItem.i)}
      >
        <div className="builder-canvas-card__header">
          <div className="builder-canvas-card__header-group">
            <span className="builder-canvas-card__badge">
              {assignedWidget?.title ?? (hasAssignment ? storedItem.label : "Empty Slot")}
            </span>
            {!assignedWidget && !hasAssignment && (
              <span className="builder-canvas-card__dimensions">
                {storedItem.w} x {storedItem.h} • min {storedItem.minW} x{" "}
                {storedItem.minH}
              </span>
            )}
          </div>

          {onRemove && (assignedWidget || hasAssignment) && (
            <div className="builder-canvas-card__controls">
              {/* Only charts that declare settings get a gear — see
                  charts/widgetConfig.ts. onRemove is passed only when the
                  dashboard is editable, so sitting inside this block gives the
                  gear the same read_only gating as the remove button. */}
              {assignedWidget &&
                hasWidgetConfigFields(assignedWidget.chartType) && (
                  <WidgetConfigMenu
                    chartType={assignedWidget.chartType}
                    config={storedItem.config}
                    onChange={handleConfigChange}
                    widgetData={resolvedWidgetData}
                    widgetLabel={assignedWidget.title}
                  />
                )}
              <button
                type="button"
                id="remove-widget-button"
                className="builder-canvas-card__action"
                aria-label={`Remove ${storedItem.label}`}
                onClick={handleRemove}
              >
                <i className="bi bi-x-lg" />
              </button>
            </div>
          )}
        </div>

        {assignedWidget ? (
          <div className="builder-canvas-card__body">
            {isInteractionActive ? (
              <div className="builder-canvas-card__preview">
                <span className="placeholder builder-canvas-card__placeholder builder-canvas-card__placeholder--block" />
                <div className="builder-canvas-card__preview-copy">
                  <strong>{assignedWidget.title}</strong>
                  <span>
                    {storedItem.w} x {storedItem.h} • moving preview
                  </span>
                </div>
              </div>
            ) : (
              <div
                className={[
                  "builder-canvas-card__content",
                  ["data_table", "map"].includes(
                    assignedWidget?.chartType ?? "",
                  ) && "builder-canvas-card__content--scrollable",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <DashboardWidgetContent
                  key={`${storedItem.i}-${storedItem.w}-${storedItem.h}-${previewConfigKey}`}
                  chartType={assignedWidget.chartType}
                  title={assignedWidget.title}
                  data={resolvedWidgetData}
                  isLoading={false}
                  hasError={false}
                  chartSpec={previewChartSpec}
                  isBuilderPreview={true}
                />
              </div>
            )}
          </div>
        ) : hasAssignment ? (
          <div className="builder-canvas-card__body">
            <div className="placeholder-glow h-100">
              <span className="placeholder w-100 h-100" />
            </div>
          </div>
        ) : (
          <div className="builder-canvas-card__body">
            <div className="builder-canvas-card__dropzone">
              <span
                className="placeholder builder-canvas-card__placeholder builder-canvas-card__placeholder--block"
                aria-hidden="true"
              />
              <div className="builder-canvas-card__dropzone-copy">
                <strong>Add a widget here</strong>
                <span>
                  Click from the sidebar or drag a widget into this slot.
                </span>
              </div>
            </div>
          </div>
        )}
      </article>
      {children}
    </div>
  );
});

export default memo(Slot);
